<template>
  <!-- Notification Permission Prompt -->
  <Transition name="slide-up">
    <div
      v-if="showPrompt && canEnable"
      class="notification-prompt"
    >
      <div class="notification-prompt__icon">
        <v-icon size="24" color="info">mdi-bell-ring-outline</v-icon>
      </div>
      <div class="notification-prompt__content">
        <div class="notification-prompt__title">Enable Notifications</div>
        <div class="notification-prompt__text">
          Get notified about new messages, likes, and comments
        </div>
      </div>
      <div class="notification-prompt__actions">
        <v-btn variant="text" size="small" @click="dismiss">
          Not now
        </v-btn>
        <v-btn
          color="info"
          size="small"
          :loading="isLoading"
          @click="enable"
        >
          Enable
        </v-btn>
      </div>
    </div>
  </Transition>

  <!-- Error Snackbar -->
  <v-snackbar
    v-model="showError"
    color="error"
    timeout="5000"
  >
    {{ error }}
    <template #actions>
      <v-btn variant="text" @click="showError = false">
        Close
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { usePushNotifications } from '@/composables/usePushNotifications'
import { useAuth } from '@/composables/useAuth'

const { isAuthenticated } = useAuth()
const {
  isSupported,
  permissionStatus,
  canEnable,
  isLoading,
  error,
  enableNotifications,
} = usePushNotifications()

const showPrompt = ref(false)
const showError = ref(false)

// Storage key for dismissal
const DISMISS_KEY = 'notification_prompt_dismissed'
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000 // 7 days

// Check if should show prompt
onMounted(() => {
  checkShouldShow()
})

// Show prompt when user logs in
watch(isAuthenticated, (authenticated) => {
  if (authenticated) {
    checkShouldShow()
  } else {
    showPrompt.value = false
  }
})

// Show error when it changes
watch(error, (newError) => {
  if (newError) {
    showError.value = true
  }
})

function checkShouldShow() {
  // Don't show if not authenticated
  if (!isAuthenticated.value) {
    showPrompt.value = false
    return
  }

  // Don't show if not supported or already granted/denied
  if (!isSupported.value || !permissionStatus.value.default) {
    showPrompt.value = false
    return
  }

  // Check if recently dismissed
  const dismissed = localStorage.getItem(DISMISS_KEY)
  if (dismissed) {
    const dismissedAt = parseInt(dismissed, 10)
    if (Date.now() - dismissedAt < DISMISS_DURATION) {
      showPrompt.value = false
      return
    }
  }

  // Show after a short delay for better UX
  setTimeout(() => {
    showPrompt.value = true
  }, 3000)
}

async function enable() {
  const success = await enableNotifications()
  if (success) {
    showPrompt.value = false
  }
}

function dismiss() {
  showPrompt.value = false
  localStorage.setItem(DISMISS_KEY, Date.now().toString())
}
</script>

<style lang="scss" scoped>
.notification-prompt {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.24);
  z-index: var(--z-dropdown);
  max-width: 420px;
  width: calc(100% - 32px);

  &__icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(var(--v-theme-info), 0.1);
    border-radius: 10px;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-weight: 600;
    font-size: 0.875rem;
    color: rgb(var(--v-theme-on-surface));
  }

  &__text {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.7);
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }
}

// Transitions
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

// Responsive
@media (max-width: 480px) {
  .notification-prompt {
    bottom: 120px;
    flex-wrap: wrap;

    &__content {
      flex-basis: calc(100% - 52px);
    }

    &__actions {
      width: 100%;
      justify-content: flex-end;
      margin-top: 8px;
    }
  }
}
</style>
